import * as React from 'react';
import { gql } from '@apollo/client';
import { UserCardController, UserCardControllerProps } from './UserCard.controller';
import {
  GetOrganizationDocument,
  useCreateUserWorkspaceAssignmentMutation,
  useRemoveUserWorkspaceAssignmentMutation,
  useResendUserInvitationMutation,
  useUpdateUserInvitationMutation,
  useUpdateUserOrganizationAssignmentMutation
} from '../../../models/graphql';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';

export type UserCardApolloProps = Omit<
  UserCardControllerProps,
  | 'updateUserInvitation'
  | 'resendUserInvitation'
  | 'updateUserOrganizationAssignment'
  | 'createUserWorkspaceAssignment'
  | 'removeUserWorkspaceAssignment'
> & {
  // ...
};

gql`
  mutation UpdateUserInvitation($userInvitationId: ID!, $input: UserInvitationInput!) {
    updateUserInvitation(userInvitationId: $userInvitationId, input: $input) {
      id
      email
      role
      workspaceIds
    }
  }

  mutation UpdateUserOrganizationAssignment(
    $userId: ID!
    $organizationId: ID!
    $input: UserOrganizationAssignmentInput!
  ) {
    updateUserOrganizationAssignment(input: $input, userId: $userId, organizationId: $organizationId) {
      id
      role
    }
  }

  mutation CreateUserWorkspaceAssignment($userId: ID!, $workspaceId: ID!) {
    createUserWorkspaceAssignment(userId: $userId, workspaceId: $workspaceId) {
      id
      name
      users {
        id
        firstName
        email
      }
      organization {
        id
      }
    }
  }

  mutation RemoveUserWorkspaceAssignment($userId: ID!, $workspaceId: ID!) {
    removeUserWorkspaceAssignment(userId: $userId, workspaceId: $workspaceId)
  }

  mutation ResendUserInvitation($userInvitationId: ID!) {
    resendUserInvitation(userInvitationId: $userInvitationId)
  }
`;

export const UserCardApollo = (props: UserCardApolloProps): JSX.Element => {
  const { selectedOrganization } = useSelectedOrganization();
  const [updateUserInvitation] = useUpdateUserInvitationMutation();

  const [updateUserOrganizationAssignment] = useUpdateUserOrganizationAssignmentMutation();

  const [createUserWorkspaceAssignment] = useCreateUserWorkspaceAssignmentMutation();

  const [removeUserWorkspaceAssignment] = useRemoveUserWorkspaceAssignmentMutation({
    refetchQueries: [
      {
        query: GetOrganizationDocument,
        variables: { organizationId: selectedOrganization?.id }
      }
    ]
  });

  const [resendUserInvitation] = useResendUserInvitationMutation();

  const controllerProps: UserCardControllerProps = {
    ...props,
    updateUserInvitation,
    updateUserOrganizationAssignment,
    createUserWorkspaceAssignment,
    removeUserWorkspaceAssignment,
    resendUserInvitation
  };

  return <UserCardController {...controllerProps} />;
};
